import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

// section_key must match backend _KEY_RE regex.
const KEY_RE = /^[a-z0-9][a-z0-9_-]{1,63}$/;

function emptyForm() {
    return {
        section_key: "",
        title_ar: "",
        title_en: "",
        subtitle_ar: "",
        subtitle_en: "",
        items_text: "",   // one item per line — cleaned server-side too
    };
}

function fromSection(row) {
    return {
        section_key: row.section_key || "",
        title_ar: row.title_ar || "",
        title_en: row.title_en || "",
        subtitle_ar: row.subtitle_ar || "",
        subtitle_en: row.subtitle_en || "",
        items_text: (row.items || []).join("\n"),
    };
}

function itemsFromText(txt) {
    return (txt || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

export default function QCSectionFormDialog({ open, onOpenChange, section, onSaved }) {
    const { t } = useI18n();
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm(section ? fromSection(section) : emptyForm());
    }, [open, section]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const items = itemsFromText(form.items_text);
    const dedupedCount = new Set(items).size;

    const validate = () => {
        if (!section && !KEY_RE.test(form.section_key.trim())) {
            toast.error(t("qc_ins_section_key_hint"));
            return false;
        }
        if (!form.title_ar.trim()) {
            toast.error(t("qc_ins_title_ar") + " — " + t("error_generic"));
            return false;
        }
        return true;
    };

    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                section_key: form.section_key.trim(),
                title_ar: form.title_ar.trim(),
                title_en: form.title_en.trim(),
                subtitle_ar: form.subtitle_ar.trim(),
                subtitle_en: form.subtitle_en.trim(),
                items,
            };
            if (section) {
                await api.patch(`/qc-inspection/sections/${section.id}`, payload);
            } else {
                await api.post(`/qc-inspection/sections`, payload);
            }
            onSaved && (await onSaved());
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={OPS.qcIns.form.dialog}
                className="max-w-2xl max-h-[88vh] overflow-y-auto scrollbar-thin"
            >
                <DialogHeader>
                    <DialogTitle>
                        {section ? t("qc_ins_edit_section") : t("qc_ins_new_section")}
                    </DialogTitle>
                    <DialogDescription>{t("qc_ins_admin_desc")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* section_key — locked in edit mode: renaming would break future violation links. */}
                    <div className="space-y-1.5">
                        <Label>{t("qc_ins_section_key")}</Label>
                        <Input
                            data-testid={OPS.qcIns.form.sectionKey}
                            value={form.section_key}
                            onChange={(e) => set("section_key", e.target.value)}
                            dir="ltr"
                            className="text-start font-mono"
                            placeholder="general_cleanliness"
                            disabled={!!section}
                        />
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("qc_ins_section_key_hint")}</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>{t("qc_ins_title_ar")}</Label>
                            <Input
                                data-testid={OPS.qcIns.form.titleAr}
                                value={form.title_ar}
                                onChange={(e) => set("title_ar", e.target.value)}
                                dir="rtl"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("qc_ins_title_en")}</Label>
                            <Input
                                data-testid={OPS.qcIns.form.titleEn}
                                value={form.title_en}
                                onChange={(e) => set("title_en", e.target.value)}
                                dir="ltr"
                                className="text-start"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("qc_ins_subtitle_ar")}</Label>
                            <Input
                                data-testid={OPS.qcIns.form.subtitleAr}
                                value={form.subtitle_ar}
                                onChange={(e) => set("subtitle_ar", e.target.value)}
                                dir="rtl"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t("qc_ins_subtitle_en")}</Label>
                            <Input
                                data-testid={OPS.qcIns.form.subtitleEn}
                                value={form.subtitle_en}
                                onChange={(e) => set("subtitle_en", e.target.value)}
                                dir="ltr"
                                className="text-start"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <Label>{t("qc_ins_items")}</Label>
                            <span className="text-[11px] text-[color:var(--ink-muted)]">
                                {t("qc_ins_items_count")(dedupedCount)}
                            </span>
                        </div>
                        <Textarea
                            data-testid={OPS.qcIns.form.items}
                            value={form.items_text}
                            onChange={(e) => set("items_text", e.target.value)}
                            placeholder={t("qc_ins_items_placeholder")}
                            className="min-h-[220px] font-sans"
                        />
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("qc_ins_items_desc")}</p>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        data-testid={OPS.qcIns.form.cancel}
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        data-testid={OPS.qcIns.form.save}
                        onClick={onSave}
                        disabled={saving}
                        className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                    >
                        {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                        {t("save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
